import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { DashboardLayout } from '../../components/dashboard';
import { Button, Page, PageHeader, Tabs } from '../../components/ui';
import { ConfidentialityPanel, LockMarker } from '../../features/confidentiality';
import { getIssue, issuesErrorMessage } from '../../features/issues/issuesApi';
import { BalanceBar } from '../../features/issues/components/BalanceBar';
import { IssueBriefDialog } from '../../features/issues/components/IssueBriefDialog';
import { PositionsBoard } from '../../features/issues/board/PositionsBoard';
import { MovementTimeline } from '../../features/issues/movement/MovementTimeline';
import { IssueMapPanel } from '../../features/issues/map/IssueMapPanel';
import { ScenarioLab } from '../../features/issues/scenarios/ScenarioLab';
import type { IssueView } from '../../types/issues';

type Tab = 'positions' | 'map' | 'movement' | 'scenarios';

const TABS: { value: Tab; label: string }[] = [
  { value: 'positions', label: 'Positions' },
  { value: 'map', label: 'Map' },
  { value: 'movement', label: 'Movement' },
  { value: 'scenarios', label: 'Scenarios' },
];

/**
 * One issue, four ways of looking at it: where everyone stands, how they are
 * connected, how they have moved, and what would happen if we moved first.
 */
export const IssueWorkspacePage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [params, setParams] = useSearchParams();
  const [issue, setIssue] = useState<IssueView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [briefOpen, setBriefOpen] = useState(false);

  const requested = params.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(
    requested && TABS.some((t) => t.value === requested) ? requested : 'positions',
  );

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setIssue(await getIssue(slug));
      setError(null);
    } catch (err) {
      setError(issuesErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const changeTab = (next: Tab) => {
    setTab(next);
    const updated = new URLSearchParams(params);
    updated.set('tab', next);
    setParams(updated, { replace: true });
  };

  return (
    <DashboardLayout>
      <Page>
        <div className="mb-3">
          <Link
            to="/issues"
            className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500 transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            All issues
          </Link>
        </div>

        {error && (
          <div className="rounded-2xl border border-neutral-200/70 bg-white p-4 text-[13px] text-neutral-600">
            {error}
          </div>
        )}

        {!error && (
          <>
            <PageHeader
              title={
                <span>
                  {loading && !issue ? 'Loading…' : (issue?.title ?? 'Issue')}
                  {issue && <LockMarker record={issue} variant="pill" className="ml-2 align-middle" />}
                </span>
              }
              meta={issue?.summary ?? undefined}
              actions={
                issue ? (
                  <Button variant="primary" size="sm" onClick={() => setBriefOpen(true)}>
                    <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                    Assessment
                  </Button>
                ) : undefined
              }
            />

            {issue && <BalanceBar issue={issue} onChange={load} />}

            <Tabs items={TABS} value={tab} onChange={changeTab} label="Issue views" className="mt-4" />

            {issue && (
              <div className="mt-4">
                {tab === 'positions' && <PositionsBoard issue={issue} onChange={load} />}
                {tab === 'map' && <IssueMapPanel issue={issue} />}
                {tab === 'movement' && <MovementTimeline issue={issue} />}
                {tab === 'scenarios' && <ScenarioLab issue={issue} />}
                {tab === 'positions' && (
                  <ConfidentialityPanel
                    className="mt-6 max-w-xl"
                    entityType="issue"
                    entityId={issue.id}
                    onChanged={load}
                  />
                )}
              </div>
            )}

            {issue && briefOpen && (
              <IssueBriefDialog issue={issue} onClose={() => setBriefOpen(false)} onApplied={load} />
            )}
          </>
        )}
      </Page>
    </DashboardLayout>
  );
};
